import safeStorage, { safeSessionStorage } from './localStorage';

/**
 * Returns the origin + pathname of document.referrer (e.g. "https://partner-site.com/register"),
 * stripping all query params to avoid leaking Firebase/auth tokens into downstream systems.
 * Falls back to window.location.origin when the referrer is empty or unparseable.
 */
export const getReferrerOrigin = () => {
  try {
    const { origin, pathname } = new URL(document.referrer);
    return `${origin}${pathname}`;
  } catch {
    return `${window.location.origin}${window.location.pathname}`;
  }
};

export const UTM_KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
];

export const CLICK_ID_KEYS = [
  'gclid', // Google Ads
  'gbraid', // Google Ads (iOS)
  'wbraid', // Google Ads (web-to-app)
  'fbclid', // Meta / Facebook
  'msclkid', // Microsoft Ads
  'ttclid', // TikTok Ads
];

// GLOBAL_KEY matches Webflow + Shopify so all three domains share one store.
// SESSION_KEY is the legacy React app key — kept for backwards compat reads.
const GLOBAL_KEY = 'utm_params_global';
const SESSION_KEY = 'utms';
const CLICK_IDS_KEY = 'click_ids';

/**
 * Returns stored UTMs + click IDs as a single flat object.
 * Priority: sessionStorage → utm_params_global (localStorage) → utms (localStorage legacy)
 */
export const getStoredUtms = () => {
  try {
    // 1. sessionStorage — fastest, set by captureUtms on this tab
    const ss = safeSessionStorage.getItem(SESSION_KEY);
    if (ss) return JSON.parse(ss);

    // 2. Unified cross-domain localStorage key (written by Webflow, Shopify, and this app)
    const global = safeStorage.getItem(GLOBAL_KEY);
    if (global) {
      // Hydrate sessionStorage so subsequent reads skip localStorage
      safeSessionStorage.setItem(SESSION_KEY, global);
      return JSON.parse(global);
    }

    // 3. Legacy React-only localStorage key (pre-fix sessions)
    const legacy = safeStorage.getItem(SESSION_KEY);
    if (legacy) {
      safeSessionStorage.setItem(SESSION_KEY, legacy);
      return JSON.parse(legacy);
    }

    return {};
  } catch {
    return {};
  }
};

/**
 * Click IDs are now merged into utm_params_global along with UTMs.
 * This reads them back out for callers that need them separately.
 */
export const getStoredClickIds = () => {
  try {
    const all = getStoredUtms();
    const ids = {};
    CLICK_ID_KEYS.forEach((k) => { if (all[k]) ids[k] = all[k]; });
    return ids;
  } catch {
    return {};
  }
};

/**
 * Called once on app mount by UtmProvider.
 * Reads UTMs + click IDs from the landing URL, merges with anything
 * already stored (URL params win), and writes to BOTH storage keys
 * so Shopify and Webflow can read utm_params_global.
 */
export const captureUtms = () => {
  const params = new URLSearchParams(window.location.search);
  const urlUtms = {};
  const urlClickIds = {};

  UTM_KEYS.forEach((key) => {
    const val = params.get(key);
    if (val) urlUtms[key] = val;
  });

  CLICK_ID_KEYS.forEach((key) => {
    const val = params.get(key);
    if (val) urlClickIds[key] = val;
  });

  const hasNewUtms = Object.keys(urlUtms).length > 0;
  const hasNewClickIds = Object.keys(urlClickIds).length > 0;
  const hasAnything = hasNewUtms || hasNewClickIds;

  if (hasAnything) {
    const merged = { ...urlUtms, ...urlClickIds };
    const serialized = JSON.stringify(merged);

    // Write to unified cross-domain key (Webflow + Shopify read this)
    safeStorage.setItem(GLOBAL_KEY, serialized);

    // Write to sessionStorage for fast in-tab reads
    safeSessionStorage.setItem(SESSION_KEY, serialized);

    // Keep legacy click_ids key for any existing callers
    if (hasNewClickIds) {
      const clickSerialized = JSON.stringify(urlClickIds);
      safeSessionStorage.setItem(CLICK_IDS_KEY, clickSerialized);
      safeStorage.setItem(CLICK_IDS_KEY, clickSerialized);
    }

    return merged;
  }

  // Nothing in URL — fall back to whatever is stored.
  // This is the cross-domain inheritance path: user lands on app.trashie.io
  // from shop.trashie.io with no UTMs in URL, we read utm_params_global
  // that Shopify wrote and use those.
  return getStoredUtms();
};

/**
 * Appends stored UTMs + click IDs to an external URL.
 * Does not overwrite params already present in the target URL.
 */
export const appendUtmParams = (url) => {
  if (!url || url === '#' || url.startsWith('#')) return url;

  try {
    const stored = getStoredUtms();
    if (!Object.keys(stored).length) return url;

    const parsed = new URL(url, window.location.origin);

    // Append UTMs
    UTM_KEYS.forEach((key) => {
      if (stored[key] && !parsed.searchParams.has(key)) {
        parsed.searchParams.set(key, stored[key]);
      }
    });

    // Append click IDs so they survive the hop
    CLICK_ID_KEYS.forEach((key) => {
      if (stored[key] && !parsed.searchParams.has(key)) {
        parsed.searchParams.set(key, stored[key]);
      }
    });

    if (url.startsWith('http://') || url.startsWith('https://')) {
      return parsed.toString();
    }
    return parsed.pathname + parsed.search + parsed.hash;
  } catch {
    return url;
  }
};
